/**
 * Replaces tokens in a template string with provided values
 * Token format: {{tokenName(defaultValue="xxx")}}
 *
 * @param template - The template string containing tokens
 * @param values - Key-value pairs for token replacement { tokenName: 'value' }
 * @param globalFallback - Fallback value when token has no defaultValue and no value provided
 * @returns Template with tokens replaced by values
 *
 * @example
 * // Event category in meta title
 * replaceTokens(
 *   '{{eventsCategoryName(defaultValue="Events")}} at The Red Lion',
 *   { eventsCategoryName: 'Live Music' },
 *   'Events'
 * )
 * // Returns: 'Live Music at The Red Lion'
 *
 * // Fallback to default when category is null (View All)
 * replaceTokens(
 *   '{{eventsCategoryName(defaultValue="Events")}} at The Red Lion',
 *   { eventsCategoryName: null },
 *   'Events'
 * )
 * // Returns: 'Events at The Red Lion'
 *
 * // Venue name with global fallback
 * replaceTokens(
 *   'Book a table at {{venueName}}',
 *   {},
 *   'Your Local Greene King Pub'
 * )
 * // Returns: 'Book a table at Your Local Greene King Pub'
 *
 * // Multiple tokens for meta description
 * replaceTokens(
 *   'Discover {{eventsCategoryName(defaultValue="events")}} at {{venueName(defaultValue="our pub")}}',
 *   { eventsCategoryName: 'Quiz Nights', venueName: "The Brig O'Don" },
 *   'events'
 * )
 * // Returns: 'Discover Quiz Nights at The Brig O\'Don'
 */
export function replaceTokens(
  template: string | null | undefined,
  values: Record<string, string | null | undefined> = {},
  globalFallback = ''
): string {
  if (!template || typeof template !== 'string') {
    return '';
  }

  const TOKEN_REGEX = /\{\{(\w+)(?:\(defaultValue="([^"]*)"\))?\}\}/g;

  return template.replaceAll(
    TOKEN_REGEX,
    (_match, tokenName: string, defaultValue: string | undefined) => {
      if (values[tokenName] !== undefined && values[tokenName] !== null) {
        return values[tokenName];
      }

      if (defaultValue !== undefined) {
        return defaultValue;
      }

      return globalFallback;
    }
  );
}
